import React, { createContext, useContext, useEffect, useState } from 'react';
import userManager from './AuthConfig';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string, token: string) => Promise<User>;
  loginWithOtp: (username: string, accessToken: string, expiresIn?: string, userData?: any) => Promise<User>;
  loginWithCallback: (accessToken: string, expiresIn?: string) => Promise<User>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export interface UserProfile {
  name: string;
  email: string;
  username: string;
  fullname: string;
  phoneNumber: string;
  address_postalcode: string;
  address_street: string;
  address_district: string;
  address_number: string;
  address_city: string;
  address_state: string;
  address_complement: string;
  address_reference: string;
}

export interface User {
  profile: UserProfile;
  access_token: string;
  id_token: string;
  expires_in: string;
  status?: string;
  updated_at?: string;
  expiration_time?: number;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const initAuth = async () => {
      const currentUser = await userManager.getUser();
      setUser(currentUser);
      setIsLoading(false);
    };
    
    initAuth();
  }, []);
  
  const login = async (username: string, password: string, token: string): Promise<User> => {
    const loggedUser = await userManager.login(username, password, token);
    setUser(loggedUser);
    
    return loggedUser;
  };
  
  // Nova função para login via OTP
  const loginWithOtp = async (username: string, accessToken: string, expiresIn?: string, userData?: any): Promise<User> => {
    try {
      let loggedUser: User;
      
      // Se userData for fornecido, usa o método direto
      if (userData) {
        loggedUser = await userManager.loginOtpWithUserData(username, accessToken, expiresIn || '3600', userData);
      } else {
        // Senão, usa o método tradicional que busca os dados via API
        loggedUser = await userManager.loginOtp(username, accessToken, expiresIn || '3600');
      }
      
      setUser(loggedUser);
      
      return loggedUser;
      
    } catch (error) {
      console.error('Erro no login via OTP:', error);
      throw error;
    }
  };
  
  const loginWithCallback = async (accessToken: string, expiresIn?: string): Promise<User> => {
    const loggedUser = await userManager.loginWithCallback(accessToken, expiresIn || '3600');
    setUser(loggedUser);

    return loggedUser;
  };

  const logout = async (): Promise<void> => {
    await userManager.signoutRedirect();
    setUser(null);
  };
  
  if (isLoading) {
    return null;
  }
  
  return (
    <AuthContext.Provider value={{user, login, loginWithOtp, loginWithCallback, logout, isLoading, error: null}}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};
